import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Paper,
  Divider,
  Alert,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  LocalOffer as CouponIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  couponManager,
  AvailableCoupon,
  AppliedCoupon,
  InvoiceItem
} from '../../services/couponManager';

// ==================== الأنواع والواجهات ====================

/** خصائص مكون التسويق */
interface MarketingSectionProps {
  initialEnabled?: boolean;
  initialCouponCodes?: string[];
  availableCoupons?: AvailableCoupon[];
  disabled?: boolean;
  invoiceItems?: InvoiceItem[];
  pageId?: string;
  onChange: (marketingData: MarketingData) => void;
}

/** بيانات التسويق المُرسلة للمكون الأب */
interface MarketingData {
  marketingEnabled: boolean;
  appliedCoupons: AppliedCoupon[];
  totalDiscount: number;
}

// ==================== الدوال المساعدة ====================

/**
 * إنشاء مفتاح فريد للكوبونات
 */
const createCouponsKey = (coupons: AvailableCoupon[]): string => {
  return coupons
    .map(c => `${c.code}:${c.discountValue}`)
    .sort()
    .join('|');
};

/**
 * إنشاء مفتاح فريد لعناصر الفاتورة
 */
const createItemsKey = (items: InvoiceItem[]): string => {
  return items
    .map(item => `${item.productId || item.id || ''}:${item.variantId || ''}:${item.quantity || 0}`)
    .sort()
    .join('|');
};

// ==================== المكون الرئيسي ====================

const MarketingSection: React.FC<MarketingSectionProps> = ({
  initialEnabled = false,
  initialCouponCodes = [],
  availableCoupons = [],
  disabled = false,
  invoiceItems = [],
  pageId,
  onChange
}) => {
  // ==================== الحالة ====================
  const [couponCode, setCouponCode] = useState('');
  const [couponEnabled, setCouponEnabled] = useState(initialCouponCodes.length > 0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [appliedCoupons, setAppliedCoupons] = useState<AppliedCoupon[]>([]);
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  
  // مراجع لتتبع الحالة السابقة ومنع التكرار
  const lastProcessedRef = useRef<string>('');
  const isProcessingRef = useRef<boolean>(false);
  const recalculationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ==================== القيم المحسوبة ====================
  const couponsKey = useMemo(() => createCouponsKey(availableCoupons), [availableCoupons]);
  const itemsKey = useMemo(() => createItemsKey(invoiceItems), [invoiceItems]);
  const appliedCouponCodes = useMemo(() => appliedCoupons.map(c => c.code), [appliedCoupons]);

  // ==================== إرسال التحديثات للمكون الأب ====================
  const notifyParent = useCallback((coupons: AppliedCoupon[]) => {
    const totalDiscount = couponManager.calculateTotalDiscount(coupons);
    onChange({
      marketingEnabled: true,
      appliedCoupons: coupons,
      totalDiscount
    });
  }, [onChange]);

  // ==================== دالة تطبيق كوبون واحد ====================
  const applySingleCoupon = useCallback(async (code: string, silent = false): Promise<boolean> => {
    if (!code?.trim()) return false;

    if (!silent) setIsVerifying(true);
    
    try {
      const result = await couponManager.applyCoupon(
        code,
        invoiceItems,
        appliedCoupons,
        pageId
      );

      if (result.success && result.coupon) {
        const newAppliedCoupons = [...appliedCoupons, result.coupon];
        setAppliedCoupons(newAppliedCoupons);
        notifyParent(newAppliedCoupons);
        
        if (!silent) {
          setVerificationMessage({
            type: 'success',
            text: result.message || 'تم تطبيق الكوبون بنجاح'
          });
          setCouponCode('');
        }
        return true;
      } else {
        if (!silent) {
          setVerificationMessage({
            type: 'error',
            text: result.message || 'فشل تطبيق الكوبون'
          });
        }
        return false;
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('خطأ في تطبيق الكوبون:', error);
        if (!silent) {
          setVerificationMessage({
            type: 'error',
            text: 'حدث خطأ أثناء تطبيق الكوبون'
          });
        }
      }
      return false;
    } finally {
      if (!silent) setIsVerifying(false);
    }
  }, [invoiceItems, appliedCoupons, pageId, notifyParent]);

  // ==================== دالة تطبيق عدة كوبونات ====================
  const applyMultipleCoupons = useCallback(async (codes: string[]) => {
    setIsApplyingAll(true);
    
    try {
      const result = await couponManager.applyMultipleCoupons(
        codes,
        invoiceItems,
        appliedCoupons,
        pageId
      );

      if (result.successfulCoupons.length > 0) {
        const newAppliedCoupons = [...appliedCoupons, ...result.successfulCoupons];
        setAppliedCoupons(newAppliedCoupons);
        notifyParent(newAppliedCoupons);
      }

      // عرض رسالة النتيجة
      if (result.successfulCoupons.length > 0 && result.failedCoupons.length === 0) {
        setVerificationMessage({
          type: 'success',
          text: `✅ تم تطبيق ${result.successfulCoupons.length} كوبون بنجاح`
        });
      } else if (result.successfulCoupons.length > 0 && result.failedCoupons.length > 0) {
        setVerificationMessage({
          type: 'success',
          text: `✅ تم تطبيق ${result.successfulCoupons.length} كوبون، ⚠️ فشل ${result.failedCoupons.length} كوبون`
        });
      } else {
        setVerificationMessage({
          type: 'error',
          text: `❌ فشل تطبيق جميع الكوبونات (${result.failedCoupons.length})`
        });
      }
    } catch (error) {
      console.error('خطأ في تطبيق الكوبونات:', error);
      setVerificationMessage({
        type: 'error',
        text: 'حدث خطأ أثناء تطبيق الكوبونات'
      });
    } finally {
      setIsApplyingAll(false);
    }
  }, [invoiceItems, appliedCoupons, pageId, notifyParent]);

  // ==================== دالة إعادة حساب الكوبونات ====================
  const recalculateAllCoupons = useCallback(async () => {
    if (appliedCoupons.length === 0) return;

    setIsApplyingAll(true);
    
    try {
      const beforeCount = appliedCoupons.length;
      const recalculatedCoupons = await couponManager.recalculateAllCoupons(
        appliedCoupons,
        invoiceItems,
        pageId
      );

      setAppliedCoupons(recalculatedCoupons);
      notifyParent(recalculatedCoupons);

      if (recalculatedCoupons.length < beforeCount) {
        const removedCount = beforeCount - recalculatedCoupons.length;
        const removedCouponCodes = appliedCoupons
          .filter(c => !recalculatedCoupons.some(rc => rc.code === c.code))
          .map(c => c.code);
        
        console.log(`🗑️ [MarketingSection] تمت إزالة ${removedCount} كوبون غير صالح:`, removedCouponCodes);
        
        // إرسال حدث مخصص للمكون الأب لحذف الكوبونات من availableCoupons
        onChange({
          marketingEnabled: true,
          appliedCoupons: recalculatedCoupons,
          totalDiscount: couponManager.calculateTotalDiscount(recalculatedCoupons),
          removedCoupons: removedCouponCodes // إضافة الكوبونات المحذوفة
        } as any);
        
        setVerificationMessage({
          type: 'success',
          text: `تم إعادة الحساب - تمت إزالة ${removedCount} كوبون غير صالح`
        });
      }
    } catch (error) {
      console.error('خطأ في إعادة حساب الكوبونات:', error);
    } finally {
      setIsApplyingAll(false);
    }
  }, [appliedCoupons, invoiceItems, pageId, notifyParent, onChange]);

  // ==================== دالة حذف كوبون ====================
  const removeCoupon = useCallback((code: string) => {
    const newAppliedCoupons = appliedCoupons.filter(c => c.code !== code);
    setAppliedCoupons(newAppliedCoupons);
    notifyParent(newAppliedCoupons);
    
    setVerificationMessage({
      type: 'success',
      text: `تم إزالة الكوبون ${code}`
    });
  }, [appliedCoupons, notifyParent]);

  // ==================== التأثيرات ====================
  
  // تطبيق الكوبونات الأولية
  useEffect(() => {
    if (initialCouponCodes && initialCouponCodes.length > 0) {
      const timer = setTimeout(() => {
        // تطبيق كل كوبون من القائمة
        initialCouponCodes.forEach(code => {
          if (code?.trim() && !appliedCouponCodes.includes(code)) {
            applySingleCoupon(code, true);
          }
        });
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [initialCouponCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  // معالجة الكوبونات المتاحة تلقائياً وإعادة الحساب عند التغيير
  useEffect(() => {
    if (isProcessingRef.current) {
      console.log('⏸️ [MarketingSection] معالجة جارية - تخطي');
      return;
    }

    const currentKey = `${couponsKey}|${itemsKey}`;
    const previousKey = lastProcessedRef.current;
    
    if (currentKey === previousKey) {
      console.log('⏸️ [MarketingSection] نفس المفتاح - تخطي');
      return;
    }

    // تحديد الكوبونات الجديدة
    const newCoupons = availableCoupons.filter(c => !appliedCouponCodes.includes(c.code));
    
    // إذا لم توجد كوبونات جديدة ولا كوبونات مطبقة، لا داعي للمعالجة
    if (newCoupons.length === 0 && appliedCoupons.length === 0) {
      lastProcessedRef.current = currentKey;
      console.log('⏸️ [MarketingSection] لا توجد كوبونات للمعالجة - تخطي');
      return;
    }

    // إلغاء المؤقت السابق إذا كان موجوداً (debounce)
    if (recalculationTimerRef.current) {
      clearTimeout(recalculationTimerRef.current);
      console.log('⏸️ [MarketingSection] إلغاء المعالجة السابقة - انتظار توقف التغييرات');
    }

    // تأخير المعالجة لمدة 400ms بعد آخر تغيير (debounce)
    const timer = setTimeout(async () => {
      isProcessingRef.current = true;
      lastProcessedRef.current = currentKey;

      console.log('🔄 [MarketingSection] بدء معالجة الكوبونات...');
      
      try {
        // إذا كانت هناك كوبونات جديدة، طبقها
        if (newCoupons.length > 0) {
          console.log(`📦 كوبونات جديدة: ${newCoupons.length}`);
          const newCouponCodes = newCoupons.map(c => c.code);
          await applyMultipleCoupons(newCouponCodes);
          console.log('✅ [MarketingSection] انتهت معالجة الكوبونات الجديدة');
        }
        // إذا تغيرت المنتجات وهناك كوبونات مطبقة، أعد حسابها
        else if (appliedCoupons.length > 0) {
          console.log(`🔄 [MarketingSection] إعادة حساب ${appliedCoupons.length} كوبون مطبق...`);
          await recalculateAllCoupons();
          console.log('✅ [MarketingSection] تم إعادة الحساب بنجاح');
        }
      } catch (error) {
        console.error('❌ [MarketingSection] خطأ في معالجة الكوبونات:', error);
      } finally {
        isProcessingRef.current = false;
      }
    }, 400); // 🔥 تحسين السرعة: تقليل المدة من 800ms إلى 400ms
    
    recalculationTimerRef.current = timer;
    
    return () => {
      if (recalculationTimerRef.current) {
        clearTimeout(recalculationTimerRef.current);
      }
    };
  }, [couponsKey, itemsKey, appliedCoupons.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // تنظيف عند إلغاء المكون
  useEffect(() => {
    return () => {
      couponManager.cancelPendingRequests();
      if (recalculationTimerRef.current) {
        clearTimeout(recalculationTimerRef.current);
      }
    };
  }, []);

  // ==================== معالجات الأحداث ====================
  
  const handleCouponEnabledChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setCouponEnabled(enabled);
    
    if (!enabled) {
      setVerificationMessage(null);
    }
  }, []);

  const handleCouponCodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(event.target.value);
    setVerificationMessage(null);
  }, []);

  const handleApplyCoupon = useCallback(() => {
    applySingleCoupon(couponCode, false);
  }, [applySingleCoupon, couponCode]);

  const handleApplyAllCoupons = useCallback(() => {
    const unappliedCoupons = availableCoupons.filter(c => !appliedCouponCodes.includes(c.code));
    
    if (unappliedCoupons.length === 0) {
      setVerificationMessage({
        type: 'error',
        text: 'جميع الكوبونات المتاحة مطبقة بالفعل'
      });
      return;
    }
    
    const codes = unappliedCoupons.map(c => c.code);
    applyMultipleCoupons(codes);
  }, [availableCoupons, appliedCouponCodes, applyMultipleCoupons]);

  // ==================== العرض ====================
  
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      {/* العنوان */}
      <Box display="flex" alignItems="center" mb={2}>
        <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h3">
          التسويق والعروض
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* الكوبونات المتاحة */}
      {availableCoupons.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'success.main' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" color="text.primary" fontWeight="medium">
                🎉 كوبونات متاحة ({availableCoupons.length}):
              </Typography>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleApplyAllCoupons}
                disabled={disabled || isApplyingAll || availableCoupons.every(c => appliedCouponCodes.includes(c.code))}
                startIcon={isApplyingAll ? <CircularProgress size={16} color="inherit" /> : <CouponIcon />}
              >
                {isApplyingAll ? 'جاري التطبيق...' : 'تطبيق الكل'}
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {availableCoupons.map((coupon, index) => (
                <Chip
                  key={`available-${index}`}
                  label={`${coupon.code} - ${coupon.description}`}
                  color={appliedCouponCodes.includes(coupon.code) ? 'info' : 'success'}
                  variant={appliedCouponCodes.includes(coupon.code) ? 'filled' : 'outlined'}
                  size="small"
                  icon={appliedCouponCodes.includes(coupon.code) ? <span>✓</span> : undefined}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* الكوبونات المطبقة */}
      {appliedCoupons.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'info.main' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" color="text.primary" fontWeight="medium">
                ✅ الكوبونات المطبقة ({appliedCoupons.length}):
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={recalculateAllCoupons}
                disabled={disabled || isApplyingAll}
                startIcon={isApplyingAll ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              >
                تحديث الخصم
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {appliedCoupons.map((coupon, index) => (
                <Chip
                  key={`applied-${index}`}
                  label={`${coupon.code} (${coupon.discount} ${coupon.discountType === 'percentage' ? '%' : 'ريال'})`}
                  color="info"
                  variant="filled"
                  size="small"
                  onDelete={() => removeCoupon(coupon.code)}
                  deleteIcon={<DeleteIcon />}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* قسم إدخال الكوبون */}
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={couponEnabled}
              onChange={handleCouponEnabledChange}
              disabled={disabled}
            />
          }
          label="استخدام كوبون خصم"
          sx={{ mb: 2 }}
        />

        {couponEnabled && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={appliedCoupons.length > 0 
                  ? `كود الكوبون (${appliedCoupons.length} مطبق)` 
                  : 'كود الكوبون'
                }
                value={couponCode}
                onChange={handleCouponCodeChange}
                disabled={disabled || isVerifying}
                placeholder={appliedCoupons.length > 0 
                  ? 'أدخل كوبون إضافي' 
                  : 'أدخل كود الكوبون'
                }
                InputProps={{
                  startAdornment: <CouponIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
                helperText={appliedCoupons.length > 0 
                  ? `الكوبونات المطبقة: ${appliedCouponCodes.join(', ')}` 
                  : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyCoupon}
                disabled={disabled || isVerifying || !couponCode.trim()}
                startIcon={isVerifying ? <CircularProgress size={20} /> : <CouponIcon />}
              >
                {isVerifying ? 'جاري التحقق...' : 'تطبيق الكوبون'}
              </Button>
            </Grid>
          </Grid>
        )}

        {/* نتيجة التحقق */}
        {verificationMessage && (
          <Alert 
            severity={verificationMessage.type} 
            sx={{ mt: 2 }}
            onClose={() => setVerificationMessage(null)}
          >
            <Typography variant="body2">
              {verificationMessage.text}
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default MarketingSection;
